// TrustLine JSON-RPC client — OTP, onboarding, and management CRUD

import { WizardData, NamedPolicy } from '../contexts/WizardContext';
import { NetworkConfig, getNetworkEngineType, isStellarNetwork } from '../config/networks';
import { normalizeStellarAddress } from './stellar';
import { getCurrentUserToken } from '../config/firebase';
import { convertMethodSignatureToPrototype } from '../utils/abiSignature';

export { convertMethodSignatureToPrototype };

const API_URL = process.env.REACT_APP_TRUSTLINE_API_URL || 'https://api.trustline.id/api/v0';

// ─── Shared types ───────────────────────────────────────────────────────────

interface JsonRpcError {
  code: number;
  message: string;
  data?: string;
}

export interface AbiEntry {
  type: string;
  name?: string;
  inputs?: { name?: string; type: string; internalType?: string }[];
  outputs?: { name?: string; type: string }[];
  stateMutability?: string;
}

export interface DAppConfig {
  id: string;
  name: string;
  type: string;
  chainId: string;
  contractAddress: string;
  abi?: AbiEntry[];
  solanaEngineConfig?: Record<string, unknown>;
  /** Not returned by the backend yet: milliseconds since the epoch, or an ISO date. */
  createdAt?: number | string;
  updatedAt?: number | string;
}

export interface DAppConfigInput {
  chainId: string;
  contractAddress: string;
  name: string;
  type: string;
  abi?: AbiEntry[];
  solanaEngineConfig?: Record<string, unknown>;
}

export interface ClientAddress {
  line1?: string;
  line2?: string;
  city?: string;
  stateOrRegion?: string;
  postalCode?: string;
  country?: string;
}

export interface ClientProfile {
  clientId: string;
  email?: string;
  createdAt?: number;
  type?: 'individual' | 'organization' | string;
  firstName?: string;
  lastName?: string;
  entityName?: string;
  title?: string;
  address?: ClientAddress;
}

export interface ClientProfileUpdate {
  type?: string;
  firstName?: string;
  lastName?: string;
  entityName?: string;
  title?: string;
  email?: string;
  address?: ClientAddress;
}

export interface PolicyRecord {
  policyId: string;
  name: string;
  type: string;
  config: Record<string, any>;
  /** Stored by the backend but not returned yet: milliseconds since the epoch, or an ISO date. */
  createdAt?: number | string;
  updatedAt?: number | string;
}

export interface PolicyInput {
  name: string;
  type: string;
  config: Record<string, any>;
}

export interface MappingRecord {
  dAppConfigId: string;
  actionId: string;
  chainId: string;
  contractAddress: string;
  type?: string;
  policyIds?: string[];
  policyNames?: string[];
  functionPrototype?: string;
  instructionSelector?: string;
  /** Stored by the backend but not returned yet. */
  createdAt?: number | string;
  updatedAt?: number | string;
}

export interface PolicyMappingInput {
  chainId: string;
  contractAddress: string;
  functionPrototype?: string;
  instructionSelector?: string;
  policyName?: string;
  policyNames?: string[];
}

export interface ServiceResult<T = unknown> {
  success: boolean;
  error?: string;
  data?: T;
}

export interface GenerateOtpServiceResult {
  success: boolean;
  idToken?: string;
  error?: string;
}

export interface VerifyOtpServiceResult {
  success: boolean;
  firebaseToken?: string;
  error?: string;
}

export interface OnboardingServiceResult {
  success: boolean;
  error?: string;
  data?: {
    success: boolean;
    clientId?: string;
    dAppConfigs?: string[];
    policies?: string[];
    mappings?: string[];
    error?: string;
  };
}

// ─── Policy type map (UI id → API type) ─────────────────────────────────────

const POLICY_TYPE_MAP: Record<string, string> = {
  'email-otp': 'UserAuthenticationPolicy',
  'multi-email-otp': 'UserMultiAuthenticationPolicy',
  'no-verification': 'PassThroughPolicy',
};

export function getPolicyTypeFromId(policyId: string): string {
  return POLICY_TYPE_MAP[policyId] || policyId;
}

export function uiPolicyTypeFromApi(apiType: string): string {
  const entry = Object.entries(POLICY_TYPE_MAP).find(([, v]) => v === apiType);
  return entry ? entry[0] : apiType;
}

// ─── Session rejected by the backend ─────────────────────────────────────────

/** The backend's answer to a token it no longer accepts (expired, revoked). */
export const AUTH_REJECTED = /invalid or expired authentication token|not authenticated|token (has )?(expired|been revoked)/i;
const authRejectedListeners = new Set<() => void>();

/** Called whenever an authenticated call is refused for its token; returns the unsubscribe function. */
export function onAuthRejected(listener: () => void): () => void {
  authRejectedListeners.add(listener);
  return () => {
    authRejectedListeners.delete(listener);
  };
}

// ─── Low-level RPC ──────────────────────────────────────────────────────────

async function rpcCall<T>(
  method: string,
  params: Record<string, unknown> = {},
  options?: { auth?: boolean; token?: string }
): Promise<ServiceResult<T>> {
  try {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };

    if (options?.auth !== false) {
      const token = options?.token ?? (await getCurrentUserToken());
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(API_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: Date.now(),
        method,
        params,
      }),
    });

    const authenticated = options?.auth !== false;
    const rejected = (message: string) => {
      if (authenticated && AUTH_REJECTED.test(message)) authRejectedListeners.forEach((l) => l());
    };

    if (!response.ok) {
      if (authenticated && response.status === 401) authRejectedListeners.forEach((l) => l());
      return {
        success: false,
        error: `HTTP error: ${response.status} ${response.statusText}`,
      };
    }

    const data = await response.json();

    if (data.error) {
      const err = data.error as JsonRpcError;
      rejected(err.message || '');
      return { success: false, error: err.message || 'JSON-RPC error' };
    }

    const result = data.result as T & { success?: boolean; error?: string };
    if (result && typeof result === 'object' && result.success === false) {
      rejected(result.error || '');
      return { success: false, error: result.error || 'Request failed' };
    }

    return { success: true, data: result };
  } catch (error: any) {
    return { success: false, error: error.message || 'Network error occurred' };
  }
}

// ─── OTP ────────────────────────────────────────────────────────────────────

export async function generateOTP(email: string): Promise<GenerateOtpServiceResult> {
  if (!email || !email.includes('@')) {
    return { success: false, error: 'Invalid email address' };
  }

  const result = await rpcCall<{
    success: boolean;
    idToken?: string;
    error?: string;
  }>('generateOTP', { email }, { auth: false });

  if (!result.success || !result.data) {
    return { success: false, error: result.error };
  }
  if (result.data.success && result.data.idToken) {
    return { success: true, idToken: result.data.idToken };
  }
  return { success: false, error: result.data.error || 'OTP generation failed' };
}

export async function verifyOTP(
  idToken: string,
  otp: string,
  email: string
): Promise<VerifyOtpServiceResult> {
  if (!idToken || !otp || !email) {
    return { success: false, error: 'Missing required parameters' };
  }
  if (!/^\d{6}$/.test(otp)) {
    return { success: false, error: 'OTP must be a 6-digit number' };
  }

  const result = await rpcCall<{
    success: boolean;
    firebaseToken?: string;
    error?: string;
  }>('verifyOTP', { idToken, otp, email }, { auth: false });

  if (!result.success || !result.data) {
    return { success: false, error: result.error };
  }
  if (result.data.success && result.data.firebaseToken) {
    return { success: true, firebaseToken: result.data.firebaseToken };
  }
  return { success: false, error: result.data.error || 'OTP verification failed' };
}

// ─── Onboarding helpers ─────────────────────────────────────────────────────

function normalizeAllowlist(entries: string[] | undefined): string[] {
  const out: string[] = [];
  (entries || []).forEach((entry) => {
    const normalized = entry.trim().toLowerCase();
    if (normalized && !out.includes(normalized)) out.push(normalized);
  });
  return out;
}

export function buildPolicyConfig(policy: NamedPolicy): Record<string, any> {
  if (policy.apiConfig) {
    return { ...policy.apiConfig };
  }

  const config: Record<string, any> = {};
  const apiType = getPolicyTypeFromId(policy.policyType);
  const isEmailAuth =
    policy.policyType === 'email-otp' ||
    policy.policyType === 'multi-email-otp' ||
    apiType === 'UserAuthenticationPolicy' ||
    apiType === 'UserMultiAuthenticationPolicy';

  if (isEmailAuth) {
    config.allowedEmailDomains = normalizeAllowlist(policy.customizations.emails);
    config.requireEmailVerification = true;
    config.allowsCustomization = true;
  }

  if (policy.policyType === 'multi-email-otp' || apiType === 'UserMultiAuthenticationPolicy') {
    config.requiredAdditionalAuthentications =
      policy.customizations.requiredAdditionalAuthentications ?? 0;
    config.allowedAdditionalAuthenticationEmailDomains = normalizeAllowlist(
      policy.customizations.validatorEmails
    );
  }

  return config;
}

function transformWizardDataToOnboardingParams(
  wizardData: WizardData,
  chain: NetworkConfig,
  email: string
) {
  const engineType = getNetworkEngineType(chain);
  const contractAddress = isStellarNetwork(chain)
    ? normalizeStellarAddress(wizardData.contractAddress)
    : wizardData.contractAddress;

  const dAppConfig: DAppConfigInput = {
    chainId: chain.chainId.toString(),
    contractAddress,
    name: wizardData.dAppName,
    type: engineType,
  };

  if (engineType === 'evm') {
    const parsed = JSON.parse(wizardData.contractABI);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      throw new Error('Contract ABI must be a non-empty JSON array');
    }
    if (!parsed.some((entry: AbiEntry) => entry.type === 'function' && entry.name)) {
      throw new Error('Contract ABI must contain at least one named function');
    }
    dAppConfig.abi = parsed;
  }

  const mappedPolicyIds = new Set(
    wizardData.methodMappings
      .filter((m) => m.policyId !== null)
      .map((m) => m.policyId as string)
  );

  const policies: PolicyInput[] = wizardData.namedPolicies
    .filter((policy) => !policy.isExisting || mappedPolicyIds.has(policy.id))
    .map((policy) => ({
      name: policy.name,
      type: policy.apiType || getPolicyTypeFromId(policy.policyType),
      config: buildPolicyConfig(policy),
    }));

  const mappings: PolicyMappingInput[] = wizardData.methodMappings
    .filter((mapping) => mapping.policyId !== null)
    .map((mapping) => {
      const policy = wizardData.namedPolicies.find((p) => p.id === mapping.policyId);
      if (!policy) {
        throw new Error(`Policy not found for mapping: ${mapping.policyId}`);
      }
      return {
        functionPrototype: convertMethodSignatureToPrototype(mapping.methodName),
        chainId: chain.chainId.toString(),
        contractAddress,
        policyName: policy.name,
      };
    });

  // Ensure every mapped policy is present in policies[] (API requirement)
  const policyNames = new Set(policies.map((p) => p.name));
  for (const mapping of mappings) {
    if (mapping.policyName && !policyNames.has(mapping.policyName)) {
      const policy = wizardData.namedPolicies.find((p) => p.name === mapping.policyName);
      if (policy) {
        policies.push({
          name: policy.name,
          type: policy.apiType || getPolicyTypeFromId(policy.policyType),
          config: buildPolicyConfig(policy),
        });
        policyNames.add(policy.name);
      }
    }
  }

  return {
    email,
    dAppConfigs: [dAppConfig],
    policies,
    mappings,
  };
}

export async function submitOnboarding(
  wizardData: WizardData,
  chain: NetworkConfig,
  email: string,
  firebaseToken?: string
): Promise<OnboardingServiceResult> {
  try {
    if (!wizardData.dAppName || !wizardData.contractAddress || !chain) {
      return { success: false, error: 'Missing required wizard data' };
    }
    if (isStellarNetwork(chain) && !wizardData.contractABI?.trim()) {
      return { success: false, error: 'Contract interface (SEP-48) has not been loaded yet' };
    }
    if (!isStellarNetwork(chain) && !wizardData.contractABI?.trim()) {
      return { success: false, error: 'Contract ABI is required' };
    }
    if (!email || !email.includes('@')) {
      return { success: false, error: 'Invalid email address' };
    }

    let params;
    try {
      params = transformWizardDataToOnboardingParams(wizardData, chain, email);
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to transform wizard data' };
    }

    const result = await rpcCall<{
      success: boolean;
      clientId?: string;
      dAppConfigs?: string[];
      policies?: string[];
      mappings?: string[];
      error?: string;
    }>('onboarding', params as unknown as Record<string, unknown>, {
      token: firebaseToken,
    });

    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }
    if (result.data.success) {
      return { success: true, data: result.data };
    }
    return { success: false, error: result.data.error || 'Onboarding failed' };
  } catch (error: any) {
    return { success: false, error: error.message || 'Network error occurred' };
  }
}

// ─── Client profile ─────────────────────────────────────────────────────────

export async function getClientProfile(): Promise<ServiceResult<{ profile: ClientProfile }>> {
  return rpcCall('getClientProfile', {});
}

export async function updateClientProfile(
  patch: ClientProfileUpdate
): Promise<ServiceResult<{ profile?: ClientProfile }>> {
  return rpcCall('updateClientProfile', patch as Record<string, unknown>);
}

export async function deleteClientProfile(): Promise<ServiceResult<{ clientId?: string; deleted?: boolean }>> {
  return rpcCall('deleteClientProfile', {});
}

// ─── dApp configs ───────────────────────────────────────────────────────────

export async function listDAppConfigs(): Promise<ServiceResult<{ dAppConfigs: DAppConfig[] }>> {
  return rpcCall('listDAppConfigs', {});
}

export async function addDAppConfig(
  dAppConfig: DAppConfigInput
): Promise<ServiceResult> {
  return rpcCall('addDAppConfig', { dAppConfig });
}

export async function updateDAppConfig(params: {
  chainId: string;
  contractAddress: string;
  name?: string;
  abi?: AbiEntry[];
  solanaEngineConfig?: Record<string, unknown>;
}): Promise<ServiceResult> {
  return rpcCall('updateDAppConfig', params as Record<string, unknown>);
}

export async function deleteDAppConfig(
  chainId: string,
  contractAddress: string
): Promise<ServiceResult> {
  return rpcCall('deleteDAppConfig', { chainId, contractAddress });
}

// ─── Policies ───────────────────────────────────────────────────────────────

export async function listPolicies(): Promise<ServiceResult<{ policies: PolicyRecord[] }>> {
  return rpcCall('listPolicies', {});
}

export async function addPolicies(
  policies: PolicyInput[]
): Promise<ServiceResult> {
  return rpcCall('addPolicies', { policies });
}

export async function updatePolicy(params: {
  policyId?: string;
  name?: string;
  type?: string;
  config?: Record<string, any>;
}): Promise<ServiceResult> {
  return rpcCall('updatePolicy', params as Record<string, unknown>);
}

export async function deletePolicy(policyId: string): Promise<ServiceResult> {
  return rpcCall('deletePolicy', { policyId });
}

// ─── Mappings ───────────────────────────────────────────────────────────────

export async function listMappings(filter?: {
  chainId?: string;
  contractAddress?: string;
}): Promise<ServiceResult<{ mappings: MappingRecord[] }>> {
  return rpcCall('listMappings', (filter || {}) as Record<string, unknown>);
}

export async function addMappings(
  mappings: PolicyMappingInput[]
): Promise<ServiceResult> {
  return rpcCall('addMappings', { mappings });
}

export async function updateMapping(params: {
  chainId: string;
  contractAddress: string;
  /** Preferred: identify existing mapping without ABI/prototype re-validation. */
  actionId?: string;
  functionPrototype?: string;
  instructionSelector?: string;
  policyNames: string[];
}): Promise<ServiceResult> {
  return rpcCall('updateMapping', params as Record<string, unknown>);
}

export async function deleteMapping(params: {
  chainId: string;
  contractAddress: string;
  /** Preferred: identify existing mapping by actionId alone. */
  actionId?: string;
  functionPrototype?: string;
  instructionSelector?: string;
}): Promise<ServiceResult> {
  return rpcCall('deleteMapping', params as Record<string, unknown>);
}
